<script setup lang="ts">
import axios from 'axios'
import { ChartColumn, ClockAlert, ExternalLink, Info, KeyRound, Pencil, Plus, Trash2 } from 'lucide-vue-next'

import ApiKeyCreateDialog from './components/ApiKeyCreateDialog.vue'
import ApiKeyRenameDialog from './components/ApiKeyRenameDialog.vue'
import ApiKeyRevealDialog from './components/ApiKeyRevealDialog.vue'
import ApiKeyRevokeDialog from './components/ApiKeyRevokeDialog.vue'
import ApiKeyUsageDialog from './components/ApiKeyUsageDialog.vue'

import Error from '@/components/errors/Error.vue'
import { API_DOC_URL } from '@/constants/apiKeys'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { ApiKeyExpiry, ApiKeyListMeta, ApiKeyStatus, ApiKeyToken, ApiKeyTokenCreated } from '@/types/apiKeys'

const MS_PER_DAY = 86_400_000
const EXPIRY_WARNING_DAYS = 14

const { t } = useI18n()
const { formatNumber } = useNumber()
const { localDate } = useDate()
const notification = useNotification()
const { error, hasError, createError } = useError()

const isLoading = ref(true)
const tokens = ref<ApiKeyToken[]>([])
const meta = ref<ApiKeyListMeta | null>(null)
const showInactive = ref(true)

const isCreateOpen = ref(false)
const isSaving = ref(false)
const revealedToken = ref<ApiKeyTokenCreated | null>(null)

const revokeTarget = ref<ApiKeyToken | null>(null)
const isRevoking = ref(false)

const renameTarget = ref<ApiKeyToken | null>(null)
const isRenaming = ref(false)

const usageTarget = ref<ApiKeyToken | null>(null)
const isUsageOpen = ref(false)

const isActiveLimitReached = computed(() => !!meta.value && meta.value.activeCount >= meta.value.maxActive)

const fetchTokens = async () => {
  isLoading.value = true
  try {
    const { data } = await axios.get<{ data: ApiKeyToken[]; meta: ApiKeyListMeta }>('/api-keys')
    tokens.value = data.data
    meta.value = data.meta
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      createError(403)
      return
    }
    notification.create('error', { text: t('pages.settings.apiKeys.errors.fetch') })
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchTokens()
})

const visibleTokens = computed(() =>
  showInactive.value ? tokens.value : tokens.value.filter((token) => token.status === 'active')
)

const isEmpty = computed(() => !isLoading.value && tokens.value.length === 0)

const daysUntil = (dateStr: string | null) => {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / MS_PER_DAY)
}

const isExpiringSoon = (token: ApiKeyToken) => {
  if (token.status !== 'active') return false
  const days = daysUntil(token.expiresAt)
  return days !== null && days <= EXPIRY_WARNING_DAYS
}

const expiringSoonLabel = (token: ApiKeyToken) => {
  const days = daysUntil(token.expiresAt)
  if (days === null) return ''
  return days <= 1
    ? t('pages.settings.apiKeys.list.expiresTomorrow')
    : t('pages.settings.apiKeys.list.expiresInDays', { count: days })
}

const formatDate = (dateStr: string | null) => (dateStr ? localDate(new Date(dateStr)) : null)

const expiresLabel = (token: ApiKeyToken) => {
  if (token.status === 'revoked') return t('pages.settings.apiKeys.list.noExpiry')
  return formatDate(token.expiresAt) ?? t('pages.settings.apiKeys.list.noExpiry')
}

const statusChipColor = (status: ApiKeyStatus): ChipColor => {
  if (status === 'active') return 'green'
  if (status === 'revoked') return 'red'
  return 'gray'
}

const statusLabel = (status: ApiKeyStatus) => {
  if (status === 'active') return t('pages.settings.apiKeys.list.statusActive')
  if (status === 'expired') return t('pages.settings.apiKeys.list.statusExpired')
  return t('pages.settings.apiKeys.list.statusRevoked')
}

const createApiKey = async (payload: { name: string; expiry: ApiKeyExpiry }) => {
  isSaving.value = true
  try {
    const { data } = await axios.post<{ data: ApiKeyTokenCreated }>('/api-keys', payload)
    tokens.value = [data.data, ...tokens.value]
    if (meta.value) meta.value = { ...meta.value, activeCount: meta.value.activeCount + 1 }
    revealedToken.value = data.data
    isCreateOpen.value = false
  } catch (error) {
    const isMaxActiveReached =
      axios.isAxiosError(error) && error.response?.data?.errors?.name?.includes('max_active_reached')
    notification.create('error', {
      text: isMaxActiveReached
        ? t('pages.settings.apiKeys.errors.maxActiveReached', { max: meta.value?.maxActive })
        : t('pages.settings.apiKeys.errors.create')
    })
  } finally {
    isSaving.value = false
  }
}

const askRevoke = (token: ApiKeyToken) => {
  revokeTarget.value = token
}

const askRename = (token: ApiKeyToken) => {
  renameTarget.value = token
}

const askUsage = (token: ApiKeyToken) => {
  usageTarget.value = token
  isUsageOpen.value = true
}

const confirmRevoke = async () => {
  if (!revokeTarget.value) return
  isRevoking.value = true
  try {
    await axios.delete(`/api-keys/${revokeTarget.value.id}`)
    const revokedId = revokeTarget.value.id
    tokens.value = tokens.value.map((token) =>
      token.id === revokedId ? { ...token, status: 'revoked' as const } : token
    )
    if (meta.value) meta.value = { ...meta.value, activeCount: Math.max(0, meta.value.activeCount - 1) }
    revokeTarget.value = null
  } catch {
    notification.create('error', { text: t('pages.settings.apiKeys.errors.revoke') })
  } finally {
    isRevoking.value = false
  }
}

const confirmRename = async (name: string) => {
  if (!renameTarget.value) return
  isRenaming.value = true
  try {
    const { data } = await axios.patch<{ data: ApiKeyToken }>(`/api-keys/${renameTarget.value.id}`, { name })
    const renamedId = renameTarget.value.id
    tokens.value = tokens.value.map((token) => (token.id === renamedId ? data.data : token))
    renameTarget.value = null
  } catch {
    notification.create('error', { text: t('pages.settings.apiKeys.errors.rename') })
  } finally {
    isRenaming.value = false
  }
}
</script>

<template>
  <div
    v-if="hasError"
    class="dotted-background flex h-screen w-full flex-col items-center justify-center bg-content-bg"
  >
    <Error :error="error" />
  </div>

  <div v-else class="flex flex-col gap-6 p-6 md:p-12">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex min-w-0 items-center gap-3">
        <BaseSupportConnection>
          <h1 class="text-3xl font-bold tracking-tight text-foreground">{{ t('pages.settings.apiKeys.title') }}</h1>
        </BaseSupportConnection>
      </div>
      <div v-if="!isEmpty" class="flex items-center gap-3">
        <span v-if="meta" class="text-sm text-muted-foreground">
          {{ t('pages.settings.apiKeys.list.activeLimit', { count: meta.activeCount, max: meta.maxActive }) }}
        </span>
        <AppTooltip
          v-if="isActiveLimitReached"
          :text="t('pages.settings.apiKeys.errors.maxActiveReached', { max: meta?.maxActive })"
        >
          <Button disabled>
            <Plus />
            {{ t('pages.settings.apiKeys.createCta') }}
          </Button>
        </AppTooltip>
        <Button v-else @click="isCreateOpen = true">
          <Plus />
          {{ t('pages.settings.apiKeys.createCta') }}
        </Button>
      </div>
    </div>

    <Alert>
      <Info />
      <AlertDescription class="flex flex-wrap items-center justify-between gap-4">
        <span>{{ t('pages.settings.apiKeys.info') }}</span>
        <a
          :href="API_DOC_URL"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center gap-1.5 font-medium whitespace-nowrap text-foreground"
        >
          {{ t('pages.settings.apiKeys.docLink') }}
          <ExternalLink class="size-3.5" />
        </a>
      </AlertDescription>
    </Alert>

    <BaseSwitch
      v-if="!isEmpty && !isLoading"
      id="show-inactive-keys"
      v-model="showInactive"
      :label="t('pages.settings.apiKeys.showInactive')"
    />

    <AppSkeleton v-if="isLoading" type="tableCell" />

    <div v-else-if="isEmpty" class="flex flex-col items-center gap-4 rounded-lg bg-muted p-10 text-center">
      <span class="flex size-12 items-center justify-center rounded-full border bg-background">
        <KeyRound class="size-5" />
      </span>
      <div class="flex max-w-md flex-col gap-1.5">
        <p class="text-base font-semibold text-foreground">{{ t('pages.settings.apiKeys.empty.title') }}</p>
        <p class="text-sm text-muted-foreground">{{ t('pages.settings.apiKeys.empty.description') }}</p>
      </div>
      <div class="flex flex-wrap items-center justify-center gap-2">
        <Button @click="isCreateOpen = true">
          <Plus />
          {{ t('pages.settings.apiKeys.empty.cta') }}
        </Button>
        <a :href="API_DOC_URL" target="_blank" rel="noopener" class="text-sm font-medium">
          {{ t('pages.settings.apiKeys.docLink') }}
        </a>
      </div>
    </div>

    <template v-else>
      <div class="hidden overflow-hidden rounded-xl border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead class="px-6">{{ t('pages.settings.apiKeys.list.name') }}</TableHead>
              <TableHead class="px-6">{{ t('pages.settings.apiKeys.list.createdBy') }}</TableHead>
              <TableHead class="px-6 text-right">{{ t('pages.settings.apiKeys.list.callsThisMonth') }}</TableHead>
              <TableHead class="px-6">{{ t('pages.settings.apiKeys.list.createdAt') }}</TableHead>
              <TableHead class="px-6">{{ t('pages.settings.apiKeys.list.lastUsed') }}</TableHead>
              <TableHead class="px-6">{{ t('pages.settings.apiKeys.list.expiry') }}</TableHead>
              <TableHead class="px-6">{{ t('pages.settings.apiKeys.list.status') }}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="token in visibleTokens" :key="token.id" :class="token.status !== 'active' && 'opacity-60'">
              <TableCell class="px-6 py-4">
                <div class="flex flex-col gap-0.5">
                  <span class="font-medium text-foreground">{{ token.name }}</span>
                  <span class="font-mono text-xs text-muted-foreground">{{ token.prefix }}</span>
                </div>
              </TableCell>
              <TableCell class="px-6 py-4 text-muted-foreground">{{ token.createdBy }}</TableCell>
              <TableCell class="px-6 py-4 text-right">
                <AppTooltip :text="t('pages.settings.apiKeys.usage.tooltip')">
                  <button
                    class="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 -mr-1.5 text-sm font-semibold tabular-nums text-foreground hover:bg-muted hover:text-primary"
                    @click="askUsage(token)"
                  >
                    <ChartColumn class="size-3.5 text-muted-foreground" />
                    {{ formatNumber(token.callsCurrentMonth) }}
                  </button>
                </AppTooltip>
              </TableCell>
              <TableCell class="px-6 py-4 text-muted-foreground">{{ formatDate(token.createdAt) }}</TableCell>
              <TableCell class="px-6 py-4 text-muted-foreground">
                {{ formatDate(token.lastUsedAt) ?? t('pages.settings.apiKeys.list.neverUsed') }}
              </TableCell>
              <TableCell class="px-6 py-4">
                <div class="flex items-center gap-2">
                  <span class="text-muted-foreground">{{ expiresLabel(token) }}</span>
                  <BaseChip v-if="isExpiringSoon(token)" color="yellow" :text="expiringSoonLabel(token)">
                    <template #leading>
                      <ClockAlert class="size-3" />
                    </template>
                  </BaseChip>
                </div>
              </TableCell>
              <TableCell class="px-6 py-4">
                <BaseChip :color="statusChipColor(token.status)" :text="statusLabel(token.status)" />
              </TableCell>
              <TableCell class="px-6 py-4 text-right">
                <div v-if="token.status === 'active'" class="flex items-center justify-end gap-2">
                  <AppTooltip :text="t('pages.settings.apiKeys.list.renameTooltip')">
                    <Button variant="outline" size="icon-sm" @click="askRename(token)">
                      <Pencil />
                    </Button>
                  </AppTooltip>
                  <AppTooltip :text="t('pages.settings.apiKeys.list.revokeTooltip')">
                    <Button variant="outline-destructive" size="icon-sm" @click="askRevoke(token)">
                      <Trash2 />
                    </Button>
                  </AppTooltip>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <ul role="list" class="flex flex-col gap-4 md:hidden">
        <li
          v-for="token in visibleTokens"
          :key="token.id"
          class="flex flex-col gap-3 rounded-md border bg-muted p-4"
          :class="token.status !== 'active' && 'opacity-60'"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 flex-col gap-0.5">
              <p class="text-sm font-semibold text-foreground">{{ token.name }}</p>
              <span class="font-mono text-xs text-muted-foreground">{{ token.prefix }}</span>
            </div>
            <BaseChip :color="statusChipColor(token.status)" :text="statusLabel(token.status)" />
          </div>

          <BaseChip v-if="isExpiringSoon(token)" color="yellow" class="w-fit" :text="expiringSoonLabel(token)">
            <template #leading>
              <ClockAlert class="size-3" />
            </template>
          </BaseChip>

          <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div class="flex flex-col gap-0.5">
              <dt class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.callsThisMonth') }}</dt>
              <dd>
                <button
                  class="inline-flex items-center gap-1 font-semibold tabular-nums text-foreground underline underline-offset-2"
                  @click="askUsage(token)"
                >
                  <ChartColumn class="size-3 text-muted-foreground" />
                  {{ formatNumber(token.callsCurrentMonth) }}
                </button>
              </dd>
            </div>
            <div class="flex flex-col gap-0.5">
              <dt class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.lastUsed') }}</dt>
              <dd class="text-foreground">
                {{ formatDate(token.lastUsedAt) ?? t('pages.settings.apiKeys.list.neverUsed') }}
              </dd>
            </div>
            <div class="flex flex-col gap-0.5">
              <dt class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.createdAt') }}</dt>
              <dd class="text-foreground">{{ formatDate(token.createdAt) }}</dd>
            </div>
            <div class="flex flex-col gap-0.5">
              <dt class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.expiry') }}</dt>
              <dd class="text-foreground">{{ expiresLabel(token) }}</dd>
            </div>
            <div class="flex flex-col gap-0.5">
              <dt class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.createdBy') }}</dt>
              <dd class="text-foreground">{{ token.createdBy }}</dd>
            </div>
          </dl>

          <div v-if="token.status === 'active'" class="flex gap-2">
            <Button variant="outline" class="flex-1" @click="askRename(token)">
              <Pencil />
              {{ t('pages.settings.apiKeys.list.rename') }}
            </Button>
            <Button variant="outline-destructive" class="flex-1" @click="askRevoke(token)">
              <Trash2 />
              {{ t('pages.settings.apiKeys.list.revoke') }}
            </Button>
          </div>
        </li>
      </ul>
    </template>

    <ApiKeyCreateDialog v-model:open="isCreateOpen" :is-saving="isSaving" @create="createApiKey" />

    <ApiKeyRevealDialog
      :open="!!revealedToken"
      :token="revealedToken"
      @update:open="(v) => !v && (revealedToken = null)"
    />

    <ApiKeyRenameDialog
      :open="!!renameTarget"
      :token="renameTarget"
      :is-saving="isRenaming"
      @update:open="(v) => !v && (renameTarget = null)"
      @rename="confirmRename"
    />

    <ApiKeyRevokeDialog
      :open="!!revokeTarget"
      :token="revokeTarget"
      :is-revoking="isRevoking"
      @update:open="(v) => !v && (revokeTarget = null)"
      @confirm="confirmRevoke"
    />

    <ApiKeyUsageDialog v-model:open="isUsageOpen" :token="usageTarget" />
  </div>
</template>
